import { useEffect } from 'react';
import {
  useProjectStore,
  useTargetId,
  useTargetType,
  useCompanyId,
  useSelectedCompany,
  type PortalTarget,
} from '../store/projectStore';
import { getCurrentTenantId } from '../store/tenantStorage';
import { useUserTenants } from './api/useUserTenants';
import type { TargetTypeType } from '@terra/shared-schemas-orchestrator';
import { useApplicationById } from './api/useApplicationById';
import { useTargets, type TargetsCatalogSliceStatus } from './useTargets';
import { isApplicationActivated } from '../utils/isApplicationActivated';
import { TARGET_TYPE } from '../utils/target-scope.utils';
import { useFeatureFlagsContext } from '../app/providers/FeatureFlagsProvider';

export function isApplicationNotFoundError(error: unknown): boolean {
  return error instanceof Error && /404/i.test(error.message);
}

export function isApplicationTenantMismatch({
  applicationTenantId,
  currentTenantId,
}: {
  applicationTenantId: string | null | undefined;
  currentTenantId: string | null;
}): boolean {
  return Boolean(
    currentTenantId &&
      applicationTenantId &&
      applicationTenantId !== currentTenantId,
  );
}

export function mergeSyncedTarget({
  target,
  existingTarget,
}: {
  target: PortalTarget;
  existingTarget?: PortalTarget | null;
}): PortalTarget {
  const incomingDiscoveryContentTypes = target.discoveryContentTypes;
  const existingDiscoveryContentTypes = existingTarget?.discoveryContentTypes;
  const discoveryContentTypes =
    incomingDiscoveryContentTypes != null &&
    incomingDiscoveryContentTypes.length > 0
      ? incomingDiscoveryContentTypes
      : (existingDiscoveryContentTypes ?? incomingDiscoveryContentTypes);

  return {
    ...target,
    domains: target.domains ?? existingTarget?.domains ?? [],
    aiKind: target.aiKind ?? existingTarget?.aiKind,
    discoveryContentTypes,
    crawlerPeriodic:
      target.crawlerPeriodic !== undefined
        ? target.crawlerPeriodic
        : existingTarget?.crawlerPeriodic,
    automationSchedule:
      target.automationSchedule !== undefined
        ? target.automationSchedule
        : existingTarget?.automationSchedule,
    supportsParallelExecution:
      target.supportsParallelExecution !== undefined
        ? target.supportsParallelExecution
        : existingTarget?.supportsParallelExecution,
    signalCategoriesInScope:
      target.signalCategoriesInScope !== undefined
        ? target.signalCategoriesInScope
        : existingTarget?.signalCategoriesInScope,
  };
}

export function canValidateCatalogTargetSync({
  companyTenantId,
  isFeatureFlagsFetched,
  isLoadingTargets,
  isFetchingTargets,
  hasFetchedTargets,
}: {
  companyTenantId: string | null | undefined;
  isFeatureFlagsFetched: boolean;
  isLoadingTargets: boolean;
  isFetchingTargets: boolean;
  hasFetchedTargets: boolean;
}): boolean {
  return Boolean(
    companyTenantId &&
      isFeatureFlagsFetched &&
      hasFetchedTargets &&
      !isLoadingTargets &&
      !isFetchingTargets,
  );
}

export function shouldClearMissingCatalogTarget({
  targetType,
  catalogSliceStatus,
}: {
  targetType: TargetTypeType;
  catalogSliceStatus: TargetsCatalogSliceStatus;
}): boolean {
  if (targetType === TARGET_TYPE.AI_APPLICATION) {
    return catalogSliceStatus.aiApplications === 'loaded';
  }

  if (targetType === TARGET_TYPE.EXTERNAL_NETWORK) {
    return catalogSliceStatus.externalNetworks === 'loaded';
  }

  if (targetType === TARGET_TYPE.INTERNAL_NETWORK) {
    return catalogSliceStatus.internalNetworks === 'loaded';
  }

  return true;
}

export function useSyncSelectedTarget() {
  const targetId = useTargetId();
  const targetType = useTargetType();
  const companyId = useCompanyId();
  const selectedCompany = useSelectedCompany();
  const updateSelectedTarget = useProjectStore(
    (state) => state.updateSelectedTarget,
  );
  const clearProject = useProjectStore((state) => state.clearProject);
  const clearTargetSelection = useProjectStore(
    (state) => state.clearTargetSelection,
  );

  const shouldSyncAppTarget =
    Boolean(targetId) && targetType === TARGET_TYPE.WEB_APPLICATION;
  const shouldSyncCatalogTarget =
    Boolean(targetId) && targetType !== TARGET_TYPE.WEB_APPLICATION;

  const {
    application,
    isLoading: isLoadingApplication,
    error: applicationError,
  } = useApplicationById({
    id: targetId,
    enabled: shouldSyncAppTarget,
  });

  const {
    targets,
    catalogSliceStatus,
    isLoading: isLoadingTargets,
    isFetching: isFetchingTargets,
    isFetched: hasFetchedTargets,
    error: targetsError,
  } = useTargets({
    companyId,
  });
  const { isFetched: isFeatureFlagsFetched } = useFeatureFlagsContext();
  const { currentTenantId: authTenantId, isFetched: isTenantsFetched } =
    useUserTenants();
  const currentTenantId = isTenantsFetched
    ? getCurrentTenantId(authTenantId)
    : null;

  const canValidateCatalogTarget = canValidateCatalogTargetSync({
    companyTenantId: selectedCompany?.tenantId,
    isFeatureFlagsFetched,
    isLoadingTargets,
    isFetchingTargets,
    hasFetchedTargets,
  });

  useEffect(() => {
    if (!targetId || !shouldSyncAppTarget || isLoadingApplication) {
      return;
    }

    if (applicationError) {
      if (isApplicationNotFoundError(applicationError)) {
        clearTargetSelection();

        return;
      }

      console.warn('[useSyncSelectedTarget] Failed to sync app target', {
        error:
          applicationError instanceof Error
            ? applicationError
            : new Error(String(applicationError)),
        props: { targetId, targetType },
      });

      return;
    }

    if (!application) {
      return;
    }

    if (!isApplicationActivated(application)) {
      clearTargetSelection();

      return;
    }

    if (
      isApplicationTenantMismatch({
        applicationTenantId: application.tenantId,
        currentTenantId,
      })
    ) {
      clearProject();

      return;
    }

    updateSelectedTarget(
      mergeSyncedTarget({
        target: {
          ...application,
          targetType,
        },
        existingTarget: useProjectStore.getState().selectedTarget,
      }),
    );
  }, [
    application,
    applicationError,
    clearProject,
    clearTargetSelection,
    isLoadingApplication,
    currentTenantId,
    shouldSyncAppTarget,
    targetId,
    targetType,
    updateSelectedTarget,
  ]);

  useEffect(() => {
    const currentSelectedTarget = useProjectStore.getState().selectedTarget;

    if (
      !targetId ||
      targetType !== TARGET_TYPE.AI_APPLICATION ||
      currentSelectedTarget?.aiKind ||
      targets.length === 0
    ) {
      return;
    }

    const catalogTarget = targets.find(
      (target) => target.id === targetId && target.targetType === targetType,
    );

    if (!catalogTarget?.aiKind) {
      return;
    }

    updateSelectedTarget(
      mergeSyncedTarget({
        target: catalogTarget,
        existingTarget: currentSelectedTarget,
      }),
    );
  }, [targetId, targetType, targets, updateSelectedTarget]);

  useEffect(() => {
    if (!targetId || !shouldSyncCatalogTarget || !canValidateCatalogTarget) {
      return;
    }

    if (targetsError) {
      console.warn('[useSyncSelectedTarget] Failed to sync catalog target', {
        error:
          targetsError instanceof Error
            ? targetsError
            : new Error(String(targetsError)),
        props: { targetId, targetType },
      });

      return;
    }

    const syncedTarget = targets.find(
      (target) => target.id === targetId && target.targetType === targetType,
    );

    if (!syncedTarget) {
      if (
        !shouldClearMissingCatalogTarget({
          targetType,
          catalogSliceStatus,
        })
      ) {
        return;
      }

      clearTargetSelection();

      return;
    }

    if (
      isApplicationTenantMismatch({
        applicationTenantId: syncedTarget.tenantId,
        currentTenantId,
      })
    ) {
      clearProject();

      return;
    }

    updateSelectedTarget(
      mergeSyncedTarget({
        target: syncedTarget,
        existingTarget: useProjectStore.getState().selectedTarget,
      }),
    );
  }, [
    canValidateCatalogTarget,
    catalogSliceStatus,
    clearProject,
    clearTargetSelection,
    currentTenantId,
    shouldSyncCatalogTarget,
    targetId,
    targetType,
    targets,
    targetsError,
    updateSelectedTarget,
  ]);
}

export {
  isApplicationNotFoundError as isTargetNotFoundError,
  isApplicationTenantMismatch as isTargetTenantMismatch,
  mergeSyncedTarget as mergeSyncedApplication,
  useSyncSelectedTarget as useSyncSelectedApplication,
};
